import { MODEL_CONFIG } from "./ModelConfig";

/**
 * Display helpers for the agent config model picker. Model metadata
 * (releasedAt, costTier, description) is defined once per model in
 * ModelConfig.js — this module only reads and formats it.
 *
 * costTier is a relative 1–4 scale ($ to $$$$) for UI comparison, not exact pricing.
 */
export const MODEL_COST_TIER_MAX = 4;

export function getModelCatalogEntry(modelId) {
  for (const [provider, models] of Object.entries(MODEL_CONFIG)) {
    const config = models[modelId];
    if (!config || typeof config !== "object") continue;
    return {
      provider,
      logoPath: models.logoPath,
      modelId,
      ...config,
    };
  }
  return null;
}

export function compareModelsByReleaseDateDesc(a, b) {
  const aTime = a.releasedAt
    ? new Date(`${a.releasedAt}T12:00:00`).getTime()
    : 0;
  const bTime = b.releasedAt
    ? new Date(`${b.releasedAt}T12:00:00`).getTime()
    : 0;
  return bTime - aTime;
}

export function sortModelsByReleaseDateNewestFirst(models) {
  return [...models].sort(compareModelsByReleaseDateDesc);
}

export function getModelCatalogGroups({ includeLegacy = false } = {}) {
  return Object.entries(MODEL_CONFIG)
    .map(([provider, models]) => ({
      provider,
      logoPath: models.logoPath,
      models: sortModelsByReleaseDateNewestFirst(
        Object.entries(models)
          .filter(
            ([key, config]) =>
              key !== "logoPath" &&
              typeof config === "object" &&
              (includeLegacy || !config.legacy)
          )
          .map(([modelId, config]) => ({
            modelId,
            provider,
            logoPath: models.logoPath,
            ...config,
          }))
      ),
    }))
    .filter((group) => group.models.length > 0);
}

export function formatModelReleaseDate(releasedAt) {
  if (!releasedAt) return null;
  return new Date(`${releasedAt}T12:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function getModelAgeDays(releasedAt, now = new Date()) {
  if (!releasedAt) return null;

  const release = new Date(`${releasedAt}T12:00:00`);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  const releaseDay = new Date(
    release.getFullYear(),
    release.getMonth(),
    release.getDate(),
    12
  );
  const diffMs = today.getTime() - releaseDay.getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

export function formatModelAgeLabel(releasedAt, now = new Date()) {
  const days = getModelAgeDays(releasedAt, now);
  if (days === null) return null;
  if (days === 1) return "1 day old";
  return `${days} days old`;
}

export function formatModelCostScale(costTier) {
  if (!costTier || costTier < 1) return null;
  const tier = Math.min(costTier, MODEL_COST_TIER_MAX);
  return "$".repeat(tier);
}
